import { useRef, useState } from 'react';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { cx } from '@emotion/css';
import { Alert, Button, IconButton, Snackbar } from '@mui/material';
import { UploadOutlined } from '@mui/icons-material';
import { useUploadPaymentInvoiceMutation } from '@/models/payments';
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface Snackbar {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const InvoiceUpload = ({ id }: { id: string }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [snackbar, setSnackbar] = useState<Snackbar>({
    open: false,
    message: '',
    severity: 'success',
  });
  const { t } = useTranslation();
  const [uploadInvoice, { isLoading }] = useUploadPaymentInvoiceMutation();

  const validateFile = (file: File) => {
    if (
      file.type !== 'application/pdf' &&
      !file.name.toLowerCase().endsWith('.pdf')
    ) {
      return t('taxi_partner.payment_details.invoice_upload.file_type_error');
    }

    if (file.size > MAX_FILE_SIZE) {
      return t('taxi_partner.payment_details.invoice_upload.file_size_error');
    }

    return null;
  };

  const handleFile = (file: File) => {
    const validationError = validateFile(file);

    if (validationError) {
      setError(validationError);
      setFile(null);
      return;
    }

    setError(null);
    setFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) handleFile(selectedFile);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) handleFile(droppedFile);
  };

  const submitFile = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const response = await uploadInvoice({ id, file });

    if (response.error) {
      const serverError =
        ('data' in response.error &&
          (response.error.data as { message?: string })?.message) ||
        t('taxi_partner.payment_details.invoice_upload.upload_error');

      setError(serverError);

      return;
    }

    setSnackbar({
      open: true,
      severity: 'success',
      message: t('taxi_partner.payment_details.invoice_upload.upload_success'),
    });
    setFile(null);
  };

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold">
          {t('taxi_partner.payment_details.invoice_upload.invoice_upload')}
        </h3>
        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
          * {t('taxi_partner.payment_details.invoice_upload.required')}
        </span>
      </div>

      <div
        onClick={() => inputRef.current?.click()}
        onDragEnter={() => setDragActive(true)}
        onDragLeave={() => setDragActive(false)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className={cx(
          'cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition bg-gray-100/50 flex flex-col gap-3 justify-center items-center',
          dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        )}
      >
        <IconButton
          sx={{ backgroundColor: '#fff', boxShadow: '1px 1px 1px #00000007' }}
        >
          <CloudUploadOutlinedIcon className="text-blue-500" />
        </IconButton>

        <div>
          <p className="font-medium">
            {t('taxi_partner.payment_details.invoice_upload.click_or_drag')}
          </p>
          <p className="text-sm text-gray-500">
            {t('taxi_partner.payment_details.invoice_upload.max_file')}
          </p>

          {file && (
            <p className="mt-2 text-sm text-green-600">
              {t('taxi_partner.payment_details.invoice_upload.selected')}:{' '}
              {file.name}
            </p>
          )}
        </div>

        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleInputChange}
      />

      <Button
        disabled={!file || isLoading}
        onClick={submitFile}
        variant="contained"
        startIcon={<UploadOutlined />}
      >
        {isLoading
          ? t('taxi_partner.payment_details.invoice_upload.uploading')
          : t('taxi_partner.payment_details.invoice_upload.upload')}
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default InvoiceUpload;
